// utils/permissions.js
import { useEffect, useState } from 'react';
import apiClient from './apiClient';
import { getMyRole as getMyRoleRoute } from './APIRoutes';

// Cheap, display-only checks against a cached user object (e.g. what
// login/register returned into localStorage). Fine for deciding what
// to render when being wrong for a moment costs nothing.
export const isAdmin = (user) =>
  !!user && (user.role === 'admin' || user.role === 'superadmin');

export const isSuperAdmin = (user) => !!user && user.role === 'superadmin';

export const hasAdminPermission = (user, permission) =>
  isSuperAdmin(user) ||
  (!!user && user.role === 'admin' && Array.isArray(user.adminPermissions) && user.adminPermissions.includes(permission));

// Server-verified role hook. Prefer this over the cached-object
// helpers above for anything that gates a REAL admin action in the
// UI (showing the bulk-approve controls, the admin-grant screen,
// etc.) rather than just cosmetic display — because a role granted
// or revoked after someone last logged in won't be reflected in their
// cached localStorage object until they log in again, but it IS
// reflected here immediately, since this asks the backend directly.
//
// Still UI-only, same as everything else in this file: the actual
// enforcement is requireAdmin()/requireSelfOrAdmin() on the server.
// This hook exists so the UI's idea of "what can I do" stays in sync
// with what the server will actually allow, not so the UI becomes the
// authority.
export function useServerRole() {
  const [role, setRole] = useState(null);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // SECURITY/REGRESSION FIX: this used to call /api/admin/me
    // unconditionally on every mount — including from Categories.js
    // (the homepage) and TraderBrand.js (public shop pages, meant to
    // be browsable by anyone via the "Share Your Store" link). Since
    // that endpoint requires a session, an anonymous visitor got a
    // 401, and apiClient's global interceptor treated that 401 as
    // "session expired" and force-redirected to /login — breaking
    // guest browsing entirely, which was the whole point of those
    // pages. Skipping the network call altogether when there's no
    // cached session at all fixes this at the source: there's nothing
    // to ask the server for a logged-out visitor, the answer is
    // always "role: user, no permissions."
    const hasCachedSession = !!localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);

    if (!hasCachedSession) {
      setRole('user');
      setAdminPermissions([]);
      setLoading(false);
      return;
    }

    apiClient
      .get(getMyRoleRoute)
      .then(({ data }) => {
        if (cancelled) return;
        setRole(data.role);
        setAdminPermissions(data.adminPermissions || []);
      })
      .catch(() => {
        // Not logged in, or the request failed — treat as a plain
        // user rather than erroring the whole page out.
        if (cancelled) return;
        setRole('user');
        setAdminPermissions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    role,
    adminPermissions,
    loading,
    isAdmin: role === 'admin' || role === 'superadmin',
    isSuperAdmin: role === 'superadmin',
    hasPermission: (perm) => role === 'superadmin' || adminPermissions.includes(perm),
  };
}
